import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useParams, Navigate, useLocation } from 'react-router-dom';
import QuienesSomos from './pages/QuienesSomos';
import Privacidad from './pages/Privacidad';
import Terminos from './pages/Terminos';
import Cookies from './pages/Cookies';
import AuthPage from './pages/AuthPage';
import VerifyEmail from './pages/VerifyEmail';
import UserProfile from './pages/UserProfile';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UploadCloud, Upload, Link as LinkIcon, Scan, ShieldCheck, AlertTriangle, 
  History, Trash2, X, FileImage, FileVideo, CheckCircle, XCircle,
  Loader2, Zap, User, Crown, Download, BookOpen, Briefcase, GraduationCap,
  Search, Shield, TrendingUp, FolderOpen, Brain, FileCheck, Cloud,
  Building2, Star, Info, ImageIcon, Wand2, Clapperboard, Sparkles
} from 'lucide-react';
import axios from 'axios';
import CookieBanner from './components/CookieBanner';
import Footer from './components/Footer';
import Onboarding from './components/Onboarding';
import Precios from './pages/Precios';
import ApiDocs from './pages/ApiDocs';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import BlogAdmin from './pages/BlogAdmin';
import JoinTeam from './pages/JoinTeam';
import EmailPrefs from './pages/EmailPrefs';
import AdminPanel from './pages/AdminPanel';
import VeritasImage from './pages/VeritasImage';
import VeritasVideo from './pages/VeritasVideo';
import VeritasContent from './pages/VeritasContent';
import Landing from './pages/Landing';

// ─── Blog route wrappers ───────────────────────────────────────────────────────
function BlogRoute({ user }) {
  const navigate = useNavigate();
  return <><Blog onBack={() => navigate('/')} onPost={(slug) => navigate(`/blog/${slug}`)} user={user} onNavigateAdmin={() => navigate('/blog/admin')} /><Footer currentPage="blog" /></>;
}
function BlogPostRoute({ user }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  return <><BlogPost slug={slug} onBack={() => navigate('/')} onBlog={() => navigate('/blog')} user={user} /><Footer currentPage="blog" /></>;
}
function BlogAdminRoute({ user }) {
  const navigate = useNavigate();
  return <><BlogAdmin onBack={() => navigate('/blog')} onPost={(slug) => navigate(`/blog/${slug}`)} user={user} /><Footer currentPage="blog" /></>;
}

// v2.1
const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── PDF Export ────────────────────────────────────────────────────────────────
const exportToPDF = (result) => {
  const isReal = result.verdict === 'real';
  const date = new Date(result.created_at).toLocaleString('es-ES');
  
  const html = `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <title>Informe Veritas Scan - ${result.file_name}</title>
      <style>
        body { font-family: 'Courier New', monospace; background: #fff; color: #111; margin: 0; padding: 40px; }
        .header { border-bottom: 3px solid #111; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: flex-end; }
        .logo { font-size: 28px; font-weight: 900; letter-spacing: -1px; }
        .logo span { color: #2563eb; }
        .meta { font-size: 11px; color: #666; text-align: right; }
        .verdict-box { padding: 24px; border-radius: 8px; margin-bottom: 24px; border: 2px solid ${isReal ? '#10b981' : '#ef4444'}; background: ${isReal ? '#f0fdf4' : '#fef2f2'}; }
        .verdict-label { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #666; margin-bottom: 8px; }
        .verdict-value { font-size: 36px; font-weight: 900; color: ${isReal ? '#059669' : '#dc2626'}; letter-spacing: 2px; }
        .confidence { margin-top: 12px; }
        .confidence-bar-bg { background: #e5e7eb; border-radius: 4px; height: 10px; margin-top: 6px; }
        .confidence-bar { background: ${isReal ? '#10b981' : '#ef4444'}; border-radius: 4px; height: 10px; width: ${result.confidence}%; }
        .section { margin-bottom: 24px; }
        .section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #666; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; margin-bottom: 12px; }
        .explanation { font-size: 14px; line-height: 1.7; color: #374151; }
        .detail { display: flex; gap: 10px; margin-bottom: 8px; font-size: 13px; color: #374151; }
        .detail-icon { color: ${isReal ? '#059669' : '#dc2626'}; flex-shrink: 0; }
        .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; display: flex; justify-content: space-between; }
        .file-info { background: #f9fafb; border-radius: 6px; padding: 12px 16px; margin-bottom: 24px; font-size: 12px; color: #6b7280; }
        @media print { body { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo">VERITAS <span>SCAN</span></div>
        <div class="meta">
          <div>Informe de análisis forense IA</div>
          <div>${date}</div>
        </div>
      </div>

      <div class="file-info">
        📄 Archivo analizado: <strong>${result.file_name}</strong> &nbsp;|&nbsp; Tipo: ${result.file_type?.toUpperCase() || 'DESCONOCIDO'}
      </div>

      <div class="verdict-box">
        <div class="verdict-label">Veredicto</div>
        <div class="verdict-value">${isReal ? '✓ CONTENIDO REAL' : '⚠ GENERADO POR IA'}</div>
        <div class="confidence">
          <div style="font-size:12px; color:#666;">Nivel de confianza: <strong>${result.confidence}%</strong></div>
          <div class="confidence-bar-bg"><div class="confidence-bar"></div></div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">Análisis detallado</div>
        <p class="explanation">${result.explanation}</p>
      </div>

      ${result.details && result.details.length > 0 ? `
      <div class="section">
        <div class="section-title">Indicadores detectados</div>
        ${result.details.map(d => `
          <div class="detail">
            <span class="detail-icon">${isReal ? '✓' : '✗'}</span>
            <span>${d}</span>
          </div>
        `).join('')}
      </div>
      ` : ''}

      <div class="footer">
        <span>Generado por Veritas Scan — veritas-scan.com</span>
        <span>Este informe es orientativo. No constituye prueba legal.</span>
      </div>
    </body>
    </html>
  `;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `veritas-scan-${result.file_name.replace(/\.[^/.]+$/, '')}-${Date.now()}.html`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── How It Works Section ────────────────────────────────────────────────────
function HowItWorksSection() {
  const steps = [
    {
      number: '01',
      title: 'Sube tu contenido',
      desc: 'Arrastra una imagen, vídeo, sube un PDF, pega un texto o introduce una URL. Soportamos JPG, PNG, GIF, WebP, MP4, MOV, AVI, WebM, PDF, DOCX y TXT.',
      icon: FolderOpen,
      color: 'text-blue-400',
      border: 'border-blue-500/30',
    },
    {
      number: '02',
      title: 'La IA lo analiza',
      desc: 'Gemini 2.5 Flash examina cientos de patrones en segundos: artefactos visuales en imágenes, inconsistencias temporales en vídeos, y marcadores lingüísticos en textos.',
      icon: Brain,
      color: 'text-purple-400',
      border: 'border-purple-500/30',
    },
    {
      number: '03',
      title: 'Recibes el veredicto',
      desc: 'En segundos obtienes: veredicto (Real / IA Generada), porcentaje de confianza, explicación detallada e indicadores concretos detectados.',
      icon: ShieldCheck,
      color: 'text-emerald-400',
      border: 'border-emerald-500/30',
    },
    {
      number: '04',
      title: 'Exporta o guarda',
      desc: 'Los usuarios Pro pueden descargar el informe en PDF y acceder a su historial completo desde cualquier dispositivo gracias a la sincronización en la nube.',
      icon: Cloud,
      color: 'text-yellow-400',
      border: 'border-yellow-500/30',
    },
  ];

  return (
    <section className="py-12 sm:py-16 border-t border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12">
          <p className="font-mono text-xs tracking-widest text-purple-500 uppercase mb-3">Proceso</p>
          <h3 className="font-heading text-2xl sm:text-3xl font-black">¿Cómo funciona?</h3>
          <p className="text-zinc-500 mt-2 max-w-md mx-auto text-sm">Cuatro pasos simples para saber si un contenido es real o generado por IA.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className={`relative bg-zinc-900 border ${step.border} rounded-xl p-6 space-y-4`}
            >
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 -right-3 w-6 h-px bg-zinc-700 z-10" />
              )}
              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-zinc-800 border border-zinc-700 ${step.color}`}>
                  <step.icon className="w-5 h-5" />
                </div>
                <span className={`font-heading text-4xl font-black opacity-20 ${step.color}`}>{step.number}</span>
              </div>
              <h4 className={`font-heading font-bold text-white text-lg`}>{step.title}</h4>
              <p className="text-zinc-400 text-sm leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Use Cases Section ─────────────────────────────────────────────────────────
function UseCasesSection() {
  const cases = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: 'Periodistas',
      desc: 'Verifica la autenticidad de imágenes y artículos antes de publicar. Evita difundir desinformación generada por IA.',
      color: 'text-blue-400',
      bg: 'bg-blue-500/10 border-blue-500/20',
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: 'Profesores',
      desc: 'Detecta trabajos y ensayos generados por ChatGPT u otras IAs. Mantén la integridad académica en tu aula.',
      color: 'text-purple-400',
      bg: 'bg-purple-500/10 border-purple-500/20',
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: 'RRHH y empresas',
      desc: 'Analiza CVs, cartas de presentación y portfolios para identificar candidatos que usan IA sin declararlo.',
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10 border-emerald-500/20',
    },
    {
      icon: <Search className="w-6 h-6" />,
      title: 'Investigadores',
      desc: 'Estudia la proliferación de contenido artificial en redes sociales y medios digitales con análisis masivo.',
      color: 'text-orange-400',
      bg: 'bg-orange-500/10 border-orange-500/20',
    },
  ];

  return (
    <section className="py-12 sm:py-16 border-t border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-10">
          <p className="font-mono text-xs tracking-widest text-blue-500 uppercase mb-3">¿Para quién es?</p>
          <h3 className="font-heading text-2xl sm:text-3xl font-black">Casos de uso reales</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cases.map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className={`p-5 rounded-xl border ${c.bg} space-y-3`}
            >
              <div className={c.color}>{c.icon}</div>
              <h4 className="font-heading font-bold text-white">{c.title}</h4>
              <p className="text-zinc-400 text-sm leading-relaxed">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Stats Section ─────────────────────────────────────────────────────────────
function StatsSection({ refreshTrigger }) {
  const [stats, setStats] = useState(null);

  const fetchStats = () => {
    axios.get(`${API_URL}/api/stats`).then(r => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (refreshTrigger > 0) fetchStats();
  }, [refreshTrigger]);

  const items = [
    { label: 'Análisis realizados', value: stats ? stats.total_scans.toLocaleString('es-ES') : '—', icon: <Scan className="w-5 h-5" />, color: 'text-blue-400' },
    { label: 'Usuarios registrados', value: stats ? stats.total_users.toLocaleString('es-ES') : '—', icon: <User className="w-5 h-5" />, color: 'text-purple-400' },
    { label: 'IAs detectadas', value: stats ? stats.ai_detected.toLocaleString('es-ES') : '—', icon: <Shield className="w-5 h-5" />, color: 'text-red-400' },
    { label: 'Precisión estimada', value: `${stats?.accuracy || 94}%`, icon: <TrendingUp className="w-5 h-5" />, color: 'text-emerald-400' },
  ];

  return (
    <section className="py-10 sm:py-12 border-t border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.1 }}
              viewport={{ once: true }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 text-center"
            >
              <div className={`flex justify-center mb-2 ${item.color}`}>{item.icon}</div>
              <div className={`font-heading text-3xl font-black mb-1 ${item.color}`}>{item.value}</div>
              <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">{item.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Module config context ─────────────────────────────────────────────────────
const ModuleContext = React.createContext({ image_enabled: true, video_enabled: true, content_enabled: true });

function useModuleConfig() { return React.useContext(ModuleContext); }

// ─── Main App ──────────────────────────────────────────────────────────────────
function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLanding, setShowLanding] = useState(() => {
    return !location.state?.skipLanding && location.pathname === '/';
  });
  const [page, setPage] = useState(() => location.state?.page || 'home');
  const [moduleConfig, setModuleConfig] = useState({ image_enabled: true, video_enabled: true, content_enabled: true });

  // Load module config on mount
  React.useEffect(() => {
    fetch(`${API_URL}/api/modules/config`)
      .then(r => r.json())
      .then(data => setModuleConfig(data))
      .catch(() => {});
  }, []);

  // Safe navigate: redirect to home if module is disabled
  const safePage = (dest) => {
    if (dest === 'image' && !moduleConfig.image_enabled) { setPage('home'); setShowLanding(false); return; }
    if (dest === 'video' && !moduleConfig.video_enabled) { setPage('home'); setShowLanding(false); return; }
    setPage(dest);
  };
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('veritas_user')); } catch { return null; }
  });
  const [usage, setUsage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [url, setUrl] = useState('');
  const [inputTab, setInputTab] = useState('file');
  const [textInput, setTextInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingMsg, setAnalyzingMsg] = useState('Analizando contenido...');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState(null);
  const [statsRefresh, setStatsRefresh] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const fileInputRef = useRef(null);

  // Scroll to top on every page change
  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  // Refresh user from API on load to always have latest plan
  useEffect(() => {
    const token = localStorage.getItem('veritas_token');
    if (token) {
      axios.get(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setUser(res.data);
          localStorage.setItem('veritas_user', JSON.stringify(res.data));
        })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    fetchUsage();
    if (user) fetchCloudHistory();
    else {
      const saved = localStorage.getItem('veritas_history');
      if (saved) { try { setHistory(JSON.parse(saved)); } catch (e) {} }
    }
    // Show onboarding for first-time visitors
    if (!localStorage.getItem('veritas_onboarding_seen')) {
      setTimeout(() => setShowOnboarding(true), 800);
    }
  }, [user]);

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.get(`${API_URL}/api/usage`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUsage(res.data);
    } catch (e) {}
  };

  const fetchCloudHistory = async () => {
    try {
      const token = localStorage.getItem('veritas_token');
      if (!token) return;
      const res = await axios.get(`${API_URL}/api/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.scans || []);
    } catch (e) {}
  };

  const handleLogin = async (userData) => {
    try {
      const token = localStorage.getItem('veritas_token');
      const res = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.setItem('veritas_user', JSON.stringify(res.data));
      setUser(res.data);
    } catch { setUser(userData); }
    setPage('home');
    fetchUsage();
  };

  const handleLogout = () => {
    localStorage.removeItem('veritas_token');
    localStorage.removeItem('veritas_user');
    setUser(null);
    setHistory([]);
    setPage('home');
    fetchUsage();
  };

  const handleDragEnter = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);
  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) analyzeFile(files[0]);
  }, []);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) analyzeFile(files[0]);
  };

  const startAnalyzingMessages = () => {
    const messages = ['Analizando contenido...', 'Procesando con IA...', 'Examinando patrones...', 'Buscando anomalías...', 'Calculando confianza...', 'Casi listo...'];
    let i = 0;
    return setInterval(() => { i = (i + 1) % messages.length; setAnalyzingMsg(messages[i]); }, 2500);
  };

  const addToHistory = (scan) => {
    if (user) {
      setHistory(prev => [scan, ...prev].slice(0, 50));
    } else {
      const saved = localStorage.getItem('veritas_history');
      const current = saved ? JSON.parse(saved) : [];
      const updated = [scan, ...current].slice(0, 50);
      localStorage.setItem('veritas_history', JSON.stringify(updated));
      setHistory(updated);
    }
  };

  const pollJobStatus = async (jobId) => {
    for (let i = 0; i < 60; i++) {
      try {
        const response = await axios.get(`${API_URL}/api/job/${jobId}`);
        const { status, result: jobResult, error: jobError } = response.data;
        if (status === 'completed' && jobResult) { setResult(jobResult); addToHistory(jobResult); setStatsRefresh(prev => prev + 1); return true; }
        else if (status === 'blocked') { setError(jobError || 'Este contenido no puede ser analizado porque infringe las políticas de uso.'); return false; }
        else if (status === 'failed') { setError(jobError || 'Error al analizar el contenido'); return false; }
        await new Promise(r => setTimeout(r, 2000));
      } catch (err) {}
    }
    setError('El análisis tardó demasiado. Por favor, intenta de nuevo.');
    return false;
  };

  const analyzeFile = async (file) => {
    setIsAnalyzing(true); setAnalyzingMsg('Analizando contenido...'); setError(null); setResult(null);
    const msgInterval = startAnalyzingMessages();
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('veritas_token');
      const response = await axios.post(`${API_URL}/api/analyze/file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        timeout: 30000
      });
      if (response.data.job_id) await pollJobStatus(response.data.job_id);
      else if (response.data.id) { setResult(response.data); addToHistory(response.data); }
      fetchUsage();
    } catch (err) { setError(err.response?.data?.detail || 'Error al analizar el archivo'); }
    finally { clearInterval(msgInterval); setIsAnalyzing(false); }
  };

  const analyzeText = async () => {
    if (!textInput.trim() || textInput.trim().length < 50) { setError('El texto debe tener al menos 50 caracteres.'); return; }
    setIsAnalyzing(true); setAnalyzingMsg('Analizando contenido...'); setError(null); setResult(null);
    const msgInterval = startAnalyzingMessages();
    try {
      const blob = new Blob([textInput], { type: 'text/plain' });
      const file = new File([blob], 'texto-analizado.txt', { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('veritas_token');
      const response = await axios.post(`${API_URL}/api/analyze/file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        timeout: 30000
      });
      if (response.data.job_id) await pollJobStatus(response.data.job_id);
      else if (response.data.id) { setResult(response.data); addToHistory(response.data); }
      fetchUsage();
    } catch (err) { setError(err.response?.data?.detail || 'Error al analizar el texto'); }
    finally { clearInterval(msgInterval); setIsAnalyzing(false); }
  };

  const analyzeUrl = async () => {
    if (!url.trim()) return;
    setIsAnalyzing(true); setAnalyzingMsg('Analizando contenido...'); setError(null); setResult(null);
    const msgInterval = startAnalyzingMessages();
    try {
      const token = localStorage.getItem('veritas_token');
      const response = await axios.post(`${API_URL}/api/analyze/url`, { url }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 30000
      });
      if (response.data.job_id) { setUrl(''); await pollJobStatus(response.data.job_id); }
      else if (response.data.id) { setResult(response.data); setUrl(''); addToHistory(response.data); }
      fetchUsage();
    } catch (err) { setError(err.response?.data?.detail || 'Error al analizar la URL'); }
    finally { clearInterval(msgInterval); setIsAnalyzing(false); }
  };

  const deleteScan = async (scanId) => {
    if (user) {
      try {
        const token = localStorage.getItem('veritas_token');
        await axios.delete(`${API_URL}/api/history/${scanId}`, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {}
    } else {
      const saved = localStorage.getItem('veritas_history');
      const current = saved ? JSON.parse(saved) : [];
      const updated = current.filter(s => s.id !== scanId);
      localStorage.setItem('veritas_history', JSON.stringify(updated));
    }
    setHistory(prev => prev.filter(s => s.id !== scanId));
    if (result?.id === scanId) setResult(null);
  };

  const clearHistory = async () => {
    if (user) {
      try {
        const token = localStorage.getItem('veritas_token');
        await axios.delete(`${API_URL}/api/history`, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {}
    } else {
      localStorage.removeItem('veritas_history');
    }
    setHistory([]);
  };

  const selectFromHistory = (scan) => { setResult(scan); setShowHistory(false); };

  const sharedFooter = <Footer onNavigate={setPage} currentPage={page} />;

  if (page === 'precios') return <><Precios onBack={() => setPage('home')} onNavigateAuth={() => setPage('auth')} onNavigateProfile={() => setPage('profile')} user={user} />{sharedFooter}</>;
  if (page === 'api-docs') return <><ApiDocs onBack={() => setPage('home')} onNavigateAuth={() => setPage('auth')} user={user} />{sharedFooter}</>;
  if (page === 'quienes-somos') return <><QuienesSomos onBack={() => setPage('home')} />{sharedFooter}</>;
  if (page === 'privacidad') return <><Privacidad onBack={() => setPage('home')} />{sharedFooter}</>;
  if (page === 'terminos') return <><Terminos onBack={() => setPage('home')} />{sharedFooter}</>;
  if (page === 'cookies') return <><Cookies onBack={() => setPage('home')} />{sharedFooter}</>;
  if (page === 'auth') return <><AuthPage onSuccess={handleLogin} onBack={() => setPage('home')} />{sharedFooter}</>;
  if (page === 'profile') return <><UserProfile user={user} usage={usage} onBack={() => setPage('home')} onLogout={handleLogout} onNavigateApiDocs={() => setPage('api-docs')} />{sharedFooter}</>;
  if (page === 'image' && moduleConfig.image_enabled) return <ModuleContext.Provider value={moduleConfig}><VeritasImage user={user} onNavigate={safePage} /></ModuleContext.Provider>;
  if (page === 'image' && !moduleConfig.image_enabled) { setPage('home'); }
  if (page === 'video' && moduleConfig.video_enabled) return <ModuleContext.Provider value={moduleConfig}><VeritasVideo user={user} onNavigate={safePage} /></ModuleContext.Provider>;
  if (page === 'video' && !moduleConfig.video_enabled) { setPage('home'); }
  if (page === 'content' && moduleConfig.content_enabled !== false) return <ModuleContext.Provider value={moduleConfig}><VeritasContent user={user} onNavigate={safePage} /></ModuleContext.Provider>;
  if (page === 'content' && moduleConfig.content_enabled === false) { setPage('home'); }

  // ── Landing page ──────────────────────────────────────────────────────────────
  if (showLanding) return (
    <Landing
      moduleConfig={moduleConfig}
      onEnter={() => setShowLanding(false)}
      onNavigate={(dest) => {
        if (dest === 'blog') { navigate('/blog'); return; }
        if (dest === 'precios') { setShowLanding(false); setPage('precios'); return; }
        if (dest === 'api-docs') { setShowLanding(false); setPage('api-docs'); return; }
        if (dest === 'privacidad') { setShowLanding(false); setPage('privacidad'); return; }
        if (dest === 'auth') { setShowLanding(false); setPage('auth'); return; }
        if (dest === 'image') { if (!moduleConfig.image_enabled) return; setPage('image'); setShowLanding(false); return; }
        if (dest === 'video') { if (!moduleConfig.video_enabled) return; setPage('video'); setShowLanding(false); return; }
        if (dest === 'content') { if (moduleConfig.content_enabled === false) return; setPage('content'); setShowLanding(false); return; }
        setShowLanding(false);
      }}
    />
  );

  return (
    <div className="min-h-screen bg-zinc-950 relative flex flex-col">
      <div className="scanlines" />
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.08), transparent 60%)' }} />

      {/* Header */}
      <header className="relative z-10 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          {/* Logo izquierda — cambia según producto activo */}
          <button onClick={() => setShowLanding(true)} className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0">
            {page === 'image'
              ? <><ImageIcon className="w-6 h-6 sm:w-7 sm:h-7 text-purple-500" /><h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">VERITAS <span className="text-purple-500">IMAGE</span></h1></>
              : <><Scan className="w-6 h-6 sm:w-7 sm:h-7 text-blue-500" /><h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">VERITAS <span className="text-blue-500">SCAN</span></h1></>
            }
          </button>

          {/* Product selector */}
          <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 rounded-xl p-1">
            <button
              onClick={() => setPage('home')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all ${
                page === 'home' ? 'bg-blue-600 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <Scan className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">SCAN</span>
            </button>
            <button
              onClick={() => setPage('image')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all ${
                page === 'image' ? 'bg-purple-600 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <ImageIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">IMAGE</span>
            </button>
            <button
              onClick={() => setPage('video')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all ${
                page === 'video' ? 'bg-red-600 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <Clapperboard className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">VIDEO</span>
            </button>
            <button
              onClick={() => setPage('content')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-mono text-xs font-bold uppercase tracking-wider transition-all ${
                page === 'content' ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">CONTENT</span>
            </button>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Usage pill — hidden on mobile */}
            {usage && !usage.is_pro && (
              <div className={`hidden lg:flex items-center gap-1 px-3 py-1 rounded-full font-mono text-xs border ${usage.remaining === 0 ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-zinc-800 border-zinc-700 text-zinc-400'}`}>
                <Zap className="w-3 h-3" />{usage.remaining}/{usage.limit}
              </div>
            )}
            {usage?.is_pro && (
              user?.plan === 'business'
                ? <div className="hidden lg:flex items-center gap-1 px-3 py-1 rounded-full font-mono text-xs bg-purple-500/10 border border-purple-500/30 text-purple-400"><Building2 className="w-3 h-3" /> Business</div>
                : <div className="hidden lg:flex items-center gap-1 px-3 py-1 rounded-full font-mono text-xs bg-yellow-500/10 border border-yellow-500/30 text-yellow-400"><Star className="w-3 h-3" /> Pro</div>
            )}
            <button onClick={() => navigate('/blog')} className="hidden sm:flex items-center gap-1 px-3 py-2 text-zinc-400 hover:text-white font-mono uppercase tracking-wider text-xs sm:text-sm transition-all">
              Blog
            </button>
            <button onClick={() => setPage('precios')} className="hidden sm:flex items-center gap-1 px-3 py-2 text-zinc-400 hover:text-white font-mono uppercase tracking-wider text-xs sm:text-sm transition-all">
              Precios
            </button>
            <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono uppercase tracking-wider text-xs sm:text-sm rounded-sm transition-all">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Historial</span>
              {history.length > 0 && <span className="px-1.5 py-0.5 bg-zinc-800 rounded-full text-xs">{history.length}</span>}
            </button>
            {user ? (
              <button onClick={() => setPage('profile')} className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 font-mono text-xs sm:text-sm rounded-sm transition-all">
                <User className="w-4 h-4" /><span className="hidden sm:inline">{user.name?.split(' ')[0]}</span>
              </button>
            ) : (
              <button onClick={() => setPage('auth')} className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-mono uppercase tracking-wider text-xs sm:text-sm rounded-sm transition-all">
                <span className="hidden sm:inline">Iniciar sesión</span>
                <span className="sm:hidden">Entrar</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 flex-1">
        {/* Main analyzer */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left */}
            <div className="space-y-6 sm:space-y-8">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 sm:space-y-4">
                <p className="font-mono text-xs tracking-widest text-emerald-500 uppercase">Detector de contenido artificial</p>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-none">¿Es real o generado por IA?</h2>
                <p className="text-zinc-400 text-base sm:text-lg max-w-md">Sube una imagen, vídeo, documento o URL para analizar si fue creado por un humano o generado por inteligencia artificial.</p>
              </motion.div>

              {/* Tabs */}
              <div className="flex border border-zinc-800 rounded-sm overflow-hidden">
                {[
                  { key: 'file', label: 'Archivo' },
                  { key: 'text', label: 'Texto' },
                  { key: 'url', label: 'URL' },
                  ...(usage?.is_pro ? [{ key: 'batch', label: user?.plan === 'business' ? 'Lotes ★★' : 'Lotes ★' }] : []),
                ].map(({ key, label }) => (
                  <button key={key} onClick={() => setInputTab(key)} className={`flex-1 py-2 font-mono text-xs uppercase tracking-wider transition-all ${
                    inputTab === key
                      ? key === 'batch' ? 'bg-yellow-500 text-black' : 'bg-blue-600 text-white'
                      : key === 'batch' ? 'text-yellow-500 hover:bg-yellow-500/10' : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900'
                  }`}>{label}</button>
                ))}
              </div>

              {/* Text input */}
              {inputTab === 'text' && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                  <textarea value={textInput} onChange={(e) => setTextInput(e.target.value)} placeholder="Pega o escribe aquí el texto que quieres analizar..." disabled={isAnalyzing} rows={10} className="w-full bg-zinc-900 border border-zinc-700 rounded-xl p-4 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-blue-500 font-mono text-sm transition-colors resize-none" />
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-600 font-mono text-xs">{textInput.length} caracteres {textInput.length < 50 && textInput.length > 0 ? '(mínimo 50)' : ''}</span>
                    <button onClick={analyzeText} disabled={isAnalyzing || textInput.trim().length < 50} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono uppercase tracking-wider px-6 py-2 rounded-sm transition-all min-w-[140px] justify-center">
                      {isAnalyzing ? <><Loader2 className="w-4 h-4 animate-spin" /><motion.span key={analyzingMsg} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs">{analyzingMsg}</motion.span></> : <><Scan className="w-4 h-4" /> Analizar</>}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* File input */}
              {inputTab === 'file' && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                  className={`relative border-2 border-dashed rounded-xl p-8 sm:p-12 transition-all duration-300 cursor-pointer overflow-hidden group ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-zinc-700 bg-zinc-900/50 hover:border-blue-500 hover:bg-zinc-900/80'} ${isAnalyzing ? 'pointer-events-none' : ''}`}
                  onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
                  onClick={() => !isAnalyzing && fileInputRef.current?.click()}
                >
                  {isAnalyzing && <motion.div className="absolute left-0 right-0 h-1 bg-gradient-to-b from-transparent via-blue-500 to-transparent" initial={{ top: '0%' }} animate={{ top: '100%' }} transition={{ repeat: Infinity, duration: 2, ease: 'linear' }} />}
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,.docx,video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska" onChange={handleFileSelect} className="hidden" />
                  <div className="flex flex-col items-center gap-4 text-center">
                    {isAnalyzing ? (
                      <><Loader2 className="w-16 h-16 text-blue-500 animate-spin" /><motion.p key={analyzingMsg} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="font-mono text-blue-400 text-sm">{analyzingMsg}</motion.p></>
                    ) : (
                      <><UploadCloud className="w-16 h-16 text-zinc-500 group-hover:text-blue-500 transition-colors" /><div><p className="font-mono text-zinc-400 mb-1">Arrastra y suelta aquí</p><p className="text-zinc-600 text-sm">o haz clic para seleccionar</p></div><p className="text-zinc-600 text-xs font-mono">JPG, PNG, GIF, WebP, PDF, TXT, DOCX</p><p className="text-zinc-600 text-xs font-mono">MP4, MOV, AVI, WebM, MKV</p></>
                    )}
                  </div>
                </motion.div>
              )}

              {/* URL input */}
              {inputTab === 'url' && (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1 relative">
                      <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                      <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && analyzeUrl()} placeholder="https://ejemplo.com/articulo o imagen.jpg" disabled={isAnalyzing} className="w-full bg-zinc-900 border border-zinc-700 rounded-xl pl-12 pr-4 py-3 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-blue-500 font-mono text-sm transition-colors" />
                    </div>
                    <button onClick={analyzeUrl} disabled={isAnalyzing || !url.trim()} className="bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-mono uppercase tracking-wider px-6 py-3 rounded-sm transition-all">
                      {isAnalyzing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Scan className="w-5 h-5" />}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Batch input */}
              {inputTab === 'batch' && (
                <BatchPanel user={user} onResults={(results) => {
                  if (results.length > 0) setResult(results[0]);
                  results.forEach(r => addToHistory(r));
                  setStatsRefresh(prev => prev + 1);
                }} />
              )}

              {/* Limit banner */}
              {usage && !usage.is_pro && usage.remaining === 0 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <Crown className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-yellow-400 text-sm font-mono">Has alcanzado el límite diario de {usage.limit} análisis para este dispositivo.</p>
                    <p className="text-zinc-500 text-xs mt-0.5">Actualiza a Pro para análisis ilimitados, o espera a mañana.</p>
                  </div>
                  <button onClick={() => setPage(user ? 'profile' : 'auth')} className="bg-yellow-500 hover:bg-yellow-400 text-black font-mono text-xs uppercase tracking-wider px-3 py-1.5 rounded transition-all whitespace-nowrap">
                    {user ? 'Ver Pro' : 'Iniciar sesión'}
                  </button>
                </motion.div>
              )}

              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <p className="text-red-400 text-sm">{error}</p>
                    <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Right: Result */}
            <div>
              <AnimatePresence mode="wait">
                {result ? <VerdictPanel result={result} onClose={() => setResult(null)} isPro={usage?.is_pro} /> : <EmptyState />}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Stats section */}
        <StatsSection refreshTrigger={statsRefresh} />

        {/* How it works */}
        <HowItWorksSection />

        {/* Use cases section */}
        <UseCasesSection />
      </main>

      <Footer onNavigate={setPage} currentPage={page} />

      <CookieBanner onNavigateCookies={() => setPage('cookies')} />
      {showOnboarding && <Onboarding onClose={() => setShowOnboarding(false)} onNavigatePrecios={() => setPage('precios')} />}

      {/* History Sidebar */}
      <AnimatePresence>
        {showHistory && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 z-40" onClick={() => setShowHistory(false)} />
            <motion.aside initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25 }} className="fixed right-0 top-0 bottom-0 w-full sm:max-w-md bg-zinc-900 border-l border-zinc-800 z-50 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-zinc-800">
                <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-500" />
                  Historial
                  {user && <span className="text-xs font-mono bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full flex items-center gap-1"><Cloud className="w-3 h-3" /> nube</span>}
                </h3>
                <div className="flex items-center gap-2">
                  {history.length > 0 && <button onClick={clearHistory} className="text-zinc-500 hover:text-red-500 transition-colors p-2"><Trash2 className="w-4 h-4" /></button>}
                  <button onClick={() => setShowHistory(false)} className="text-zinc-500 hover:text-white transition-colors p-2"><X className="w-5 h-5" /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {history.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="font-mono text-sm">No hay análisis previos</p>
                    {!user && <p className="text-xs mt-2 text-zinc-600">Inicia sesión para guardar en la nube</p>}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {history.map((scan) => (
                      <HistoryItem key={scan.id} scan={scan} onSelect={() => selectFromHistory(scan)} onDelete={() => deleteScan(scan.id)} />
                    ))}
                  </div>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function BatchPanel({ user, onResults }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const batchLimit = user?.plan === 'business' ? 50 : 10;

  const handleAnalyze = async () => {
    if (!files.length) return;
    setLoading(true); setError(null); setResults([]); setProgress(0);
    try {
      const token = localStorage.getItem('veritas_token');
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const res = await axios.post(`${API_URL}/api/analyze/batch`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
        timeout: 120000
      });
      const jobIds = res.data.job_ids;
      const done = [];
      for (let idx = 0; idx < jobIds.length; idx++) {
        const jobId = jobIds[idx];
        for (let i = 0; i < 60; i++) {
          const j = await axios.get(`${API_URL}/api/job/${jobId}`);
          if (j.data.status === 'completed') { done.push(j.data.result); break; }
          if (j.data.status === 'blocked' || j.data.status === 'failed') { done.push({ error: true, file_name: files[idx]?.name || 'Archivo', reason: j.data.error }); break; }
          await new Promise(r => setTimeout(r, 2000));
        }
        setProgress(Math.round(((idx + 1) / jobIds.length) * 100));
      }
      setResults(done);
      onResults(done.filter(r => !r.error));
      setFiles([]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error en el análisis por lotes');
    } finally {
      setLoading(false);
    }
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,.docx" className="hidden"
        onChange={e => { setFiles(Array.from(e.target.files).slice(0, batchLimit)); setResults([]); }} />

      {/* Drop zone */}
      <div
        onClick={() => !loading && inputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); setFiles(Array.from(e.dataTransfer.files).slice(0, batchLimit)); setResults([]); }}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
          loading ? 'pointer-events-none border-zinc-700' : 'border-yellow-500/40 hover:border-yellow-500 hover:bg-yellow-500/5'
        }`}
      >
        {loading ? (
          <div className="space-y-3">
            <Loader2 className="w-10 h-10 text-yellow-500 animate-spin mx-auto" />
            <p className="font-mono text-yellow-400 text-sm">Analizando... {progress}%</p>
            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden max-w-xs mx-auto">
              <motion.div className="h-full bg-yellow-500 rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
            </div>
          </div>
        ) : (
          <>
            <Upload className="w-10 h-10 text-yellow-500/60 mx-auto mb-3" />
            <p className="font-mono text-zinc-400 text-sm">Arrastra hasta <strong className="text-yellow-400">{batchLimit} archivos</strong> o haz clic</p>
            <p className="font-mono text-zinc-600 text-xs mt-1">JPG, PNG, PDF, DOCX, TXT</p>
          </>
        )}
      </div>

      {/* File list */}
      {files.length > 0 && !loading && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2">
              <FileImage className="w-4 h-4 text-zinc-500 flex-shrink-0" />
              <span className="flex-1 text-zinc-300 text-xs font-mono truncate">{f.name}</span>
              <span className="text-zinc-600 text-xs font-mono">{(f.size / 1024).toFixed(0)} KB</span>
              <button onClick={() => removeFile(i)} className="text-zinc-600 hover:text-red-400 transition-colors"><X className="w-3 h-3" /></button>
            </div>
          ))}
          <button onClick={handleAnalyze}
            className={`w-full py-3 text-black font-mono text-sm uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 ${user?.plan === 'business' ? 'bg-purple-500 hover:bg-purple-400' : 'bg-yellow-500 hover:bg-yellow-400'}`}>
            <Scan className="w-4 h-4" /> Analizar {files.length} archivo{files.length > 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-2">
          <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Resultados del lote</p>
          {results.map((r, i) => r && !r.error ? (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border text-sm font-mono ${
              r.verdict === 'real' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'
            }`}>
              {r.thumbnail
                ? <img src={`data:image/jpeg;base64,${r.thumbnail}`} alt="" className="w-10 h-10 object-cover rounded" />
                : <FileImage className="w-10 h-10 text-zinc-600 p-1" />
              }
              <div className="flex-1 min-w-0">
                <p className="text-zinc-200 truncate text-xs">{r.file_name}</p>
                <p className={`text-xs mt-0.5 ${r.verdict === 'real' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {r.verdict === 'real' ? 'REAL' : 'IA GENERADA'} · {r.confidence}% confianza
                </p>
              </div>
              <CheckCircle className={`w-5 h-5 flex-shrink-0 ${r.verdict === 'real' ? 'text-emerald-500' : 'text-red-500'}`} />
            </div>
          ) : (
            <div key={i} className="flex items-center gap-2 p-3 rounded-xl border border-red-500/30 bg-red-500/5 text-red-400 text-xs font-mono">
              <X className="w-4 h-4" /> Error al analizar {r?.file_name || `archivo ${i + 1}`}
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-red-400 text-xs font-mono bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>}
    </motion.div>
  );
}

function EmptyState() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col items-center justify-center text-center py-24">
      <div className="w-24 h-24 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-6">
        <Scan className="w-10 h-10 text-zinc-600" />
      </div>
      <h3 className="font-heading text-xl font-bold text-zinc-400 mb-2">Esperando contenido</h3>
      <p className="text-zinc-600 max-w-xs">Sube una imagen o documento para comenzar el análisis de autenticidad</p>
    </motion.div>
  );
}

function VerdictPanel({ result, onClose, isPro }) {
  const isReal = result.verdict === 'real';
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className={`bg-zinc-900 border rounded-lg overflow-hidden shadow-2xl ${isReal ? 'border-emerald-500/30' : 'border-red-500/30'}`}>
      <div className={`p-4 sm:p-6 ${isReal ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {result.thumbnail ? (
              <img src={`data:image/jpeg;base64,${result.thumbnail}`} alt="Preview" className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-sm flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-zinc-800 rounded-sm flex items-center justify-center flex-shrink-0">
                {result.file_type === 'video'
                  ? <FileVideo className="w-6 h-6 sm:w-8 sm:h-8 text-zinc-600" />
                  : <FileImage className="w-6 h-6 sm:w-8 sm:h-8 text-zinc-600" />}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-1">Resultado</p>
              <p className="text-zinc-300 text-sm truncate">{result.file_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isPro && (
              <button onClick={() => exportToPDF(result)} title="Exportar informe" className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 font-mono text-xs rounded transition-all">
                <Download className="w-3 h-3" /> <span className="hidden sm:inline">PDF</span>
              </button>
            )}
            <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
          </div>
        </div>
      </div>

      <div className="p-6 text-center border-b border-zinc-800">
        <div className="inline-flex items-center gap-3 mb-4">
          {isReal ? <ShieldCheck className="w-10 h-10 text-emerald-500" /> : <AlertTriangle className="w-10 h-10 text-red-500" />}
          <span className={`font-heading text-3xl font-black tracking-widest uppercase ${isReal ? 'text-emerald-500' : 'text-red-500'}`}>
            {isReal ? 'REAL' : 'IA GENERADA'}
          </span>
        </div>
        <div className="max-w-xs mx-auto">
          <div className="flex justify-between text-xs font-mono text-zinc-500 mb-2"><span>Confianza</span><span>{result.confidence}%</span></div>
          <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${result.confidence}%` }} transition={{ duration: 1, ease: 'easeOut' }} className={`h-full rounded-full ${isReal ? 'bg-emerald-500' : 'bg-red-500'}`} />
          </div>
        </div>
      </div>

      <div className="p-6 border-b border-zinc-800">
        <h4 className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-3">Explicación</h4>
        <p className="text-zinc-300 leading-relaxed">{result.explanation}</p>
      </div>

      {result.reasoning && (
        <div className="p-6 border-b border-zinc-800">
          <h4 className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-3">Razonamiento del análisis</h4>
          <p className="text-zinc-400 text-sm leading-relaxed whitespace-pre-line">{result.reasoning}</p>
        </div>
      )}

      {result.details && result.details.length > 0 && (
        <div className="p-6">
          <h4 className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-3">Detalles detectados</h4>
          <ul className="space-y-2">
            {result.details.map((detail, index) => (
              <motion.li key={index} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.1 }} className="flex items-start gap-3 text-zinc-400">
                {isReal ? <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                <span className="text-sm">{detail}</span>
              </motion.li>
            ))}
          </ul>
          {!isPro && (
          <p className="mt-4 text-xs text-zinc-600 font-mono border-t border-zinc-800 pt-3 flex items-center gap-1.5">
          <Star className="w-3 h-3 text-yellow-500/60" /> <span className="text-zinc-500">Usuarios Pro pueden exportar este informe como PDF</span>
          </p>
          )}
        </div>
      )}
    </motion.div>
  );
}

function HistoryItem({ scan, onSelect, onDelete }) {
  const isReal = scan.verdict === 'real';
  const date = new Date(scan.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="group relative flex items-center gap-4 p-3 rounded-lg hover:bg-zinc-800/50 transition-colors cursor-pointer" onClick={onSelect}>
      <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r ${isReal ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {scan.thumbnail ? (
        <img src={`data:image/jpeg;base64,${scan.thumbnail}`} alt="Preview" className="w-12 h-12 object-cover rounded-sm" />
      ) : (
        <div className="w-12 h-12 bg-zinc-800 rounded-sm flex items-center justify-center">{scan.file_type === 'video' ? <FileVideo className="w-5 h-5 text-zinc-600" /> : <FileImage className="w-5 h-5 text-zinc-600" />}</div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-zinc-300 text-sm truncate">{scan.file_name}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-xs font-mono uppercase tracking-wider ${isReal ? 'text-emerald-500' : 'text-red-500'}`}>{isReal ? 'Real' : 'IA'}</span>
          <span className="text-zinc-600 text-xs">•</span>
          <span className="text-zinc-500 text-xs font-mono">{scan.confidence}%</span>
        </div>
      </div>
      <p className="font-mono text-xs text-zinc-500">{date}</p>
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 p-1 text-zinc-500 hover:text-red-500 transition-all">
        <Trash2 className="w-4 h-4" />
      </button>
    </motion.div>
  );
}

// ─── Scroll to top on route change ──────────────────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ─── Root with Routes ───────────────────────────────────────────────────────────
function Root() {
  const [user, setUser] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('veritas_user')); } catch { return null; }
  });

  // Keep user in sync across all routes
  React.useEffect(() => {
    const token = localStorage.getItem('veritas_token');
    if (!token) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) { setUser(data); localStorage.setItem('veritas_user', JSON.stringify(data)); } })
      .catch(() => {});
  }, []);

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/blog" element={<BlogRoute user={user} />} />
        <Route path="/blog/admin" element={<BlogAdminRoute user={user} />} />
        <Route path="/blog/:slug" element={<BlogPostRoute user={user} />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/join-team" element={<JoinTeam onNavigateHome={() => navigate('/')} />} />
        <Route path="/email-prefs" element={<EmailPrefs />} />
        <Route path="/admin" element={
          user ? (
            <AdminPanel
              user={user}
              onBack={() => window.history.back()}
              onLogout={() => { localStorage.clear(); window.location.href = '/'; }}
            />
          ) : (
            <Navigate to="/" replace />
          )
        } />
        <Route path="*" element={<App />} />
      </Routes>
    </>
  );
}

export default Root;
